/**
 * ReportCyclePanel — the CMS preliminary → draft → final report cycle
 * (Phase III). Issuing a version freezes the report exactly as it stood at
 * that moment; the Word download of a version is that frozen snapshot, never
 * the live state. The server enforces the order (draft needs a preliminary,
 * final needs a draft) and answers 409 with a plain reason when it is out of
 * order; non-blocking warnings (e.g. unclassified findings at draft time)
 * are surfaced but do not stop the issue. The old single report bar is
 * absorbed here: the live working report keeps its Preview / Word / PDF.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { REPORT_KIND_LABELS } from './labels.js';

const API = process.env.REACT_APP_API_URL;
const ORDER = ['preliminary', 'draft', 'final'];

export default function ReportCyclePanel({ auditId, onEmail, onChanged }) {
  const [reports, setReports] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/reports`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setReports(r.reports || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const issued = new Set(reports.map(r => r.kind));
  const next = ORDER.find(k => !issued.has(k));
  const latestDraft = reports.find(r => r.kind === 'draft'); // list arrives newest first

  const issue = async () => {
    if (!next) return;
    setBusy(true); setErr(''); setWarnings([]);
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/reports/${next}/issue`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'The report could not be issued.');
      setWarnings(d.warnings || []);
      await load();
      if (onChanged) await onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const saveComments = async () => {
    if (!comments.trim()) return;
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/reports/draft/comments`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ comments }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Could not record the comments.');
      setCommentsOpen(false); setComments('');
      await load();
      if (onChanged) await onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800">Report cycle</div>
          <div className="text-[11px] text-slate-400">Preliminary first, then the draft for auditee comment, then the final. Each issued version is frozen exactly as sent.</div>
        </div>
        <div className="flex gap-2">
          <a href={`${API}/api/audit/audits/${auditId}/report`} target="_blank" rel="noreferrer" className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">Preview</a>
          <a href={`${API}/api/audit/audits/${auditId}/report?format=docx`} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">↓ Word</a>
          <a href={`${API}/api/audit/audits/${auditId}/report?format=pdf`} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 text-white shadow">↓ PDF</a>
        </div>
      </div>

      {/* Stepper: which versions exist, and the one legal next issue. */}
      <div className="flex items-center gap-1 flex-wrap">
        {ORDER.map((k, i) => (
          <React.Fragment key={k}>
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${
              issued.has(k) ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : k === next ? 'bg-orange-100 text-orange-700 border-orange-200'
              : 'bg-slate-50 text-slate-400 border-slate-200'
            }`}>
              {issued.has(k) && <span aria-hidden="true">✓ </span>}{REPORT_KIND_LABELS[k]}
            </span>
            {i < ORDER.length - 1 && <span className={`w-3 h-px ${issued.has(k) ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
          </React.Fragment>
        ))}
        {next
          ? <button type="button" onClick={issue} disabled={busy} className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
              {busy ? 'Issuing…' : `Issue the ${REPORT_KIND_LABELS[next].toLowerCase()} report`}
            </button>
          : <span className="ml-auto text-[11px] font-semibold text-emerald-700">Final report issued</span>}
      </div>

      {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
      {warnings.length > 0 && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-0.5">
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {/* Issued versions, newest first — each downloads its frozen snapshot. */}
      {reports.length > 0 && (
        <div className="space-y-1.5">
          {reports.map(r => (
            <div key={r.id} className="flex items-center gap-2 flex-wrap border border-slate-100 rounded-lg px-3 py-2">
              <span className="text-xs font-semibold text-slate-700">{REPORT_KIND_LABELS[r.kind] || r.kind} report</span>
              <span className="text-[11px] text-slate-400">issued {r.issued_at ? new Date(r.issued_at).toLocaleDateString() : ''}</span>
              {r.has_comments && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Auditee comments recorded</span>}
              <span className="ml-auto flex items-center gap-3">
                <a href={`${API}/api/audit/audits/${auditId}/reports/${r.id}.docx`} className="text-[11px] font-semibold text-slate-600 hover:text-slate-800">↓ Word</a>
                {onEmail && <button type="button" onClick={() => onEmail(r)} className="text-[11px] font-semibold text-orange-600">Email to auditee</button>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Auditee comments on the latest draft. */}
      {latestDraft && (
        !commentsOpen
          ? <button type="button" onClick={() => setCommentsOpen(true)} className="text-[11px] font-semibold text-orange-600">
              {latestDraft.has_comments ? 'Update the auditee comments' : 'Record auditee comments on the draft'}
            </button>
          : (
            <div className="space-y-1.5">
              <textarea value={comments} onChange={e => setComments(e.target.value)} rows={4} placeholder="Paste the auditee's written comments on the draft report…" className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2" />
              <div className="flex gap-2">
                <button type="button" onClick={saveComments} disabled={busy || !comments.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-800 text-white shadow disabled:opacity-50">Save comments</button>
                <button type="button" onClick={() => setCommentsOpen(false)} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600">Cancel</button>
              </div>
            </div>
          )
      )}
    </div>
  );
}
